import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PlusIcon } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  type BadgeProps,
  type ColumnDef,
} from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import type { ContractListItemDto, ContractStatus, PlayerDto } from '@/api/generated/model';
import { usePlayerContractsQuery } from '@/shared/hooks/usePlayerContractsQuery';
import { formatDate, formatMoney } from '@/shared/lib/format';

const STATUS_VARIANT: Record<ContractStatus, BadgeProps['variant']> = {
  draft: 'secondary',
  in_review: 'warning',
  out_for_signature: 'info',
  executed: 'success',
  expired: 'warning',
  terminated: 'danger',
};

export interface PlayerContractsPanelProps {
  player: Pick<PlayerDto, 'id' | 'fullName'>;
  /** Fewer columns and no header card, for embedding in another step. */
  compact?: boolean;
}

/**
 * Every contract naming this player, read from the contracts inventory.
 *
 * The player page does not create or amend contracts; it links to the
 * inventory, which is where the terms, documents and signatures live. The
 * badge here is a local copy of the inventory's colours rather than an import
 * of its component, because `players` must not import `contracts` back.
 */
export function PlayerContractsPanel({ player, compact = false }: PlayerContractsPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = usePlayerContractsQuery(player.id);

  const columns = useMemo<ColumnDef<ContractListItemDto>[]>(
    () => [
      {
        id: 'title',
        header: t('players.contracts.columns.contract'),
        cell: ({ row: { original } }) => (
          <div className="grid gap-0.5 leading-tight">
            <span className="font-medium">{original.title}</span>
            <span className="text-xs text-muted-foreground">
              {original.reference} · {t(`contracts.type.${original.type}`)} ·{' '}
              {original.counterpartyLabel}
            </span>
          </div>
        ),
      },
      {
        id: 'status',
        header: t('players.contracts.columns.status'),
        meta: { width: 150 },
        cell: ({ row: { original } }) => (
          <Badge variant={STATUS_VARIANT[original.status]}>
            {t(`contracts.status.${original.status}`)}
          </Badge>
        ),
      },
      {
        id: 'term',
        header: t('players.contracts.columns.term'),
        meta: { width: 220 },
        cell: ({ row: { original } }) =>
          `${formatDate(original.startDate)} → ${formatDate(original.endDate)}`,
      },
      ...(compact
        ? []
        : [
            {
              id: 'value',
              header: t('players.contracts.columns.value'),
              meta: { align: 'end' as const, width: 150 },
              cell: ({ row: { original } }: { row: { original: ContractListItemDto } }) =>
                formatMoney(original.annualValue, original.currency),
            },
          ]),
    ],
    [t, compact],
  );

  const table = (
    <DataTable<ContractListItemDto>
      plain
      columns={columns}
      data={query.data?.data}
      getRowId={(row) => row.id}
      loading={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      onRowClick={(row) =>
        void navigate({ to: '/contracts/$contractId', params: { contractId: row.id } })
      }
      emptyState={
        <p className="py-6 text-center text-sm text-muted-foreground">
          {t('players.contracts.empty')}
        </p>
      }
    />
  );

  if (compact) return table;

  return (
    <Card className="gap-3 overflow-hidden pb-0">
      <CardHeader>
        <CardTitle>{t('players.contracts.title')}</CardTitle>
        <CardAction>
          <Button
            size="sm"
            onClick={() => void navigate({ to: '/contracts/new', search: { playerId: player.id } })}
          >
            <PlusIcon />
            {t('players.contracts.start')}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="px-0">{table}</CardContent>
    </Card>
  );
}
